import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  TextField,
  DialogActions,
} from "@mui/material";
import api from "../../../api/api";
import { getProjectRoles } from "../../../api/meta";

export default function TeamTab({ project }) {
  const [members, setMembers] = useState(project?.members || []);
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  const [form, setForm] = useState({
    user_id: "",
    role: "",
    allocation_percent: 100,
  });

  // update members when parent project changes
  useEffect(() => {
    setMembers(project?.members || []);
  }, [project]);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, [members]);

  const loadUsers = async () => {
    try {
      const res = await api.get("/users");

      // ✅ remove already assigned users
      const availableUsers = res.data.filter(
        (u) => !members.some((m) => m.user_id === u.id)
      );

      setUsers(availableUsers);
    } catch (err) {
      console.error("Error loading users", err);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await getProjectRoles();
      setRoles(data.roles);
      if (data.roles.length) {
        setForm((prev) => ({ ...prev, role: data.roles[0] }));
      }
    } catch (err) {
      console.error("Error loading roles", err);
    }
  };

  const assignMember = async () => {
    if (!form.user_id) {
      alert("Please select a user");
      return;
    }

    try {
      const res = await api.post(`/projects/${project.id}/members`, {
        user_id: form.user_id,
        project_role: form.role,
        allocation_percent: form.allocation_percent,
      });

      // ✅ add member instantly to UI
      const addedUser = users.find((u) => u.id === form.user_id);

      const newMember = {
        user_id: form.user_id,
        name: addedUser?.name,
        email: addedUser?.email,
        role_in_project: form.role,
        allocation_percentage: form.allocation_percent,
      };

      setMembers((prev) => [...prev, newMember]);

      setOpen(false);
      setForm({
        user_id: "",
        role: roles[0] || "",
        allocation_percent: 100,
      });
    } catch (err) {
      console.error("Assignment error:", err);

      if (err.response?.status === 409) {
        alert("User already assigned (updated instead).");
      } else {
        alert("Unable to assign member");
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Team Members</Typography>

        <Button variant="contained" onClick={() => setOpen(true)}>
          Add Member
        </Button>
      </Box>

      {members.length === 0 ? (
        <Typography color="text.secondary">
          No members assigned.
        </Typography>
      ) : (
        members.map((member) => (
          <Card key={member.user_id} sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight="bold">
                {member.name || member.email}
              </Typography>

              <Box display="flex" gap={1} mt={1}>
                <Chip label={member.role_in_project} size="small" />
                <Chip
                  label={`${member.allocation_percentage}%`}
                  size="small"
                  color="primary"
                />
              </Box>
            </CardContent>
          </Card>
        ))
      )}

      {/* Add Member Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>

        <DialogContent>
          {/* User Select */}
          <TextField
            select
            fullWidth
            label="User"
            margin="normal"
            value={form.user_id}
            onChange={(e) =>
              setForm({ ...form, user_id: e.target.value })
            }
          >
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name || u.email}
              </MenuItem>
            ))}
          </TextField>

          {/* Role Dropdown */}
          <TextField
            select
            fullWidth
            label="Role"
            margin="normal"
            value={form.role}
            onChange={(e) =>
              setForm({ ...form, role: e.target.value })
            }
          >
            {roles.map((r) => (
              <MenuItem key={r} value={r}>
                {r.replace("_", " ")}
              </MenuItem>
            ))}
          </TextField>

          {/* Allocation */}
          <TextField
            fullWidth
            type="number"
            label="Allocation %"
            margin="normal"
            value={form.allocation_percent}
            onChange={(e) =>
              setForm({
                ...form,
                allocation_percent: e.target.value,
              })
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={assignMember}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}